import { useEffect, useRef, useState } from 'react';
import { ConsentDemo } from '../components/ConsentDemo';
import { LoadingPage } from './LoadingPage';
import { SuccessPage } from './SuccessPage';
import { useTenant } from '../FeatureFlags';
import { getInitialParam } from '../utils/initialParams';
import { hasExternalPanelParam } from '../config/panels';
import { ErrorCode, isDuplicateAccountCode } from '../lib/errorCodes';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const SCOPES =
  'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile';

function googleRedirectUri(): string {
  return window.location.origin + '/connect/gmail-callback';
}

// "Maybe later" only supports the TapResearch panel (tid) for now — same
// customer id tap-connect already uses for its completion callback
// (see panelUrls.ts), just the "disqualified" leg instead of "complete".
function tapResearchDisqualifiedUrl(tid: string): string {
  return `https://www.tapresearch.com/router/customers/c6775533adff1b255dd27c1446db44d3/cps/disqualified?tid=${encodeURIComponent(tid)}`;
}

type State = 'priming' | 'loading' | 'success' | 'error';

function useGisLoaded(): boolean {
  const [loaded, setLoaded] = useState(!!window.google?.accounts?.oauth2);
  useEffect(() => {
    if (loaded) return;
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    const onLoad = () => setLoaded(true);
    if (existing) {
      existing.addEventListener('load', onLoad);
      return () => existing.removeEventListener('load', onLoad);
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.onload = onLoad;
    document.head.appendChild(script);
  }, [loaded]);
  return loaded;
}

export function GmailConnectPage({ onSuccess }: { onSuccess?: () => void }) {
  const tenant = useTenant();
  const gisLoaded = useGisLoaded();
  // Same external-completion gate every other retailer hook uses (e.g.
  // useSafewayDataConnection) — Gmail just never wired it up, so the panel
  // completion redirect in SuccessPage's effect never fired.
  const isExternalCompletionUser = hasExternalPanelParam({
    rid: getInitialParam('rid'),
    psid: getInitialParam('psid'),
    tid: getInitialParam('tid'),
  });
  const [state, setState] = useState<State>('priming');
  const [errorMessage, setErrorMessage] = useState(
    'Something went wrong. Please try again.'
  );
  const [errorCode, setErrorCode] = useState<string | undefined>();
  // Google reuses the signed-in account unless asked to show the chooser, so a
  // user rejected as ineligible would loop on the same account forever. Only set
  // after such a rejection — the first attempt keeps the one-click happy path.
  const [forceAccountChooser, setForceAccountChooser] = useState(false);
  // The address the server checked, so the rejection screen can name it.
  const [checkedEmail, setCheckedEmail] = useState<string | undefined>();
  const clientRef = useRef<{ requestCode: () => void } | null>(null);
  const exchangeStarted = useRef(false);

  const exchange = async (code: string) => {
    setState('loading');
    const redirectUri = googleRedirectUri();
    try {
      const res = await fetch('/api/gmail-oauth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Panel refs come from sessionStorage, not the server session: the
        // OAuth callback is a full page load, which mints a new session id.
        body: JSON.stringify({
          code,
          redirectUri,
          tid: getInitialParam('tid'),
          player: getInitialParam('player'),
          rid: getInitialParam('rid'),
          psid: getInitialParam('psid'),
          pid: getInitialParam('pid'),
          supplier_id: getInitialParam('supplier_id'),
          rsid: getInitialParam('rsid'),
          participant_id: getInitialParam('participant_id'),
          study_id: getInitialParam('study_id'),
        }),
      });
      if (res.ok) {
        const payload = (await res.json().catch(() => null)) as {
          data?: { redirectUrl?: string | null };
        } | null;
        const redirectUrl = payload?.data?.redirectUrl;
        if (redirectUrl) {
          window.location.replace(redirectUrl);
          return;
        }
        window.history.replaceState(null, '', '/connect/success');
        setState('success');
        onSuccess?.();
        return;
      }
      let message = 'Something went wrong. Please try again.';
      let responseCode: string | undefined;
      try {
        const body = (await res.json()) as {
          error?: string;
          code?: string;
          email?: string;
        };
        if (body?.error) message = body.error;
        responseCode = body?.code;
        setCheckedEmail(body?.email);
      } catch {
        /* keep default */
      }
      if (res.status === 503) {
        window.location.replace('/connect/waiting');
        return;
      }
      window.history.replaceState(null, '', '/connect/signin');
      setErrorMessage(message);
      setErrorCode(responseCode);
      if (responseCode === ErrorCode.PURCHASE_THRESHOLD_NOT_MET)
        setForceAccountChooser(true);
      setState('error');
    } catch {
      window.history.replaceState(null, '', '/connect/signin');
      setErrorMessage('Network error. Please try again.');
      setErrorCode(undefined);
      setState('error');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    if (!code && !error) return;
    if (error) {
      window.history.replaceState(null, '', '/connect/signin');
      setErrorMessage(
        error === 'access_denied'
          ? "You'll need to allow access to continue."
          : `Google sign-in was interrupted. Please try again. (error: ${error})`
      );
      setState('error');
      return;
    }
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;
    void exchange(code as string);
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  const startOauth = () => {
    const clientId = tenant.googleClientId;
    if (!gisLoaded || !window.google || !clientId) {
      setErrorMessage(
        'Google sign-in is not available right now. Please try again.'
      );
      setState('error');
      return;
    }
    clientRef.current = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: SCOPES,
      ux_mode: 'redirect',
      redirect_uri: googleRedirectUri(),
      select_account: forceAccountChooser,
    });
    clientRef.current.requestCode();
  };

  if (state === 'loading') {
    return (
      <LoadingPage autoComplete={false} totalSteps={3} retailerName="Gmail" />
    );
  }
  if (state === 'success') {
    return (
      <SuccessPage
        retailerName="Gmail"
        isExternalCompletionUser={isExternalCompletionUser}
      />
    );
  }

  if (state === 'error') {
    const notEligible = errorCode === ErrorCode.PURCHASE_THRESHOLD_NOT_MET;
    const tid = getInitialParam('tid');

    if (notEligible) {
      const retry = () => {
        setErrorCode(undefined);
        setState('priming');
      };
      return (
        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-[440px] w-full text-center">
            <div className="warn-ring mx-auto mb-7">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-[30px] h-[30px]"
              >
                <rect x="3" y="5" width="18" height="14" rx="3" />
                <path d="M3 8.5 12 14l9-5.5" />
              </svg>
            </div>
            <div className="text-[13px] font-semibold uppercase tracking-[0.18em] text-primary mb-3">
              Inbox checked
            </div>
            <h2 className="font-display text-[28px] sm:text-[32px] font-bold text-ink tracking-[-0.02em] leading-[1.2] mb-2">
              Not enough receipts in this inbox
            </h2>
            <p
              className={`text-[16px] text-ink-muted leading-[1.65] max-w-[440px] mx-auto ${checkedEmail ? 'mb-6' : 'mb-8'}`}
            >
              {errorMessage}
            </p>
            {checkedEmail && (
              <div className="bg-paper border border-rule-light rounded-xl px-[18px] py-1 mb-8 max-w-[400px] mx-auto text-left">
                <div className="flex justify-between items-center gap-4 py-3 text-[14px]">
                  <span className="text-ink-muted">Gmail account</span>
                  <span className="text-ink font-medium text-right break-all">
                    {checkedEmail}
                  </span>
                </div>
              </div>
            )}
            <div className="flex flex-col items-center gap-3.5">
              <button
                data-testid="gmail-error-connect-another-button"
                onClick={retry}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-primary hover:bg-primary-deep text-white font-semibold text-[16px] rounded-full sm:rounded-xl cursor-pointer transition-colors"
              >
                Try another Google account
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="w-[18px] h-[18px]"
                >
                  <path
                    d="M9 5l7 7-7 7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {tid && (
                <a
                  href={tapResearchDisqualifiedUrl(tid)}
                  className="text-[15px] font-medium text-ink-muted hover:text-ink px-3 py-1.5 no-underline"
                >
                  Maybe later
                </a>
              )}
            </div>
            <p className="text-[13px] leading-[1.55] text-ink-faint mt-9 max-w-[400px] mx-auto">
              We didn&apos;t keep anything from this inbox, and it&apos;s
              already disconnected.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center space-y-6">
          <img
            src={tenant.pageLogo}
            alt={tenant.appName}
            className="h-8 w-auto object-contain mx-auto"
          />
          <h2 className="font-display text-2xl font-bold text-ink">
            {isDuplicateAccountCode(errorCode)
              ? 'Account already connected'
              : 'Something went wrong'}
          </h2>
          <p className="text-ink-muted max-w-sm mx-auto">{errorMessage}</p>
          <button
            onClick={() => {
              setErrorCode(undefined);
              setState('priming');
            }}
            className="w-full bg-primary hover:bg-primary-deep text-white px-4 py-3 rounded-xl font-semibold transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // priming
  return (
    <div className="flex-1 flex items-center justify-center py-7 px-5">
      <div className="w-full max-w-[420px] text-center">
        <h1 className="font-display text-2xl font-bold text-ink tracking-[-0.02em] mb-2">
          One quick step on the next screen
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] mb-1">
          Google will ask for your permission on the screen below. Please check
          both boxes so we can find your receipts.
        </p>
        <div className="flex justify-center mt-5">
          <ConsentDemo />
        </div>
        <button
          data-testid="start-oauth-button"
          id="start-oauth-button"
          onClick={startOauth}
          className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary-deep text-white font-semibold text-[15px] rounded-xl cursor-pointer transition-colors"
        >
          Got it, continue
        </button>
      </div>
    </div>
  );
}
